/**
 * ChatDrawer - Slide-out drawer for the Ask Hydrologiq chatbot
 */

import { useRef, useEffect, useState, KeyboardEvent } from "react"
import {
  Box,
  Flex,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  IconButton,
  Spinner,
  Link,
  Badge,
  Wrap,
  WrapItem,
  Separator,
} from "@chakra-ui/react"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import { FaXmark, FaPaperPlane, FaRobot } from "react-icons/fa6"
import { ChatMessage, AIEntity, ChatExampleQA } from "./types"
import { useChatExamples } from "./useChatExamples"
import { CloseButton, CollapseButton, ExpandButton } from "@hydrologiq/shared/content/components/Button"
import { ChatButton } from "./ChatButton"

interface ChatDrawerProps {
  open: boolean
  onClose: () => void
  messages: ChatMessage[]
  isLoading: boolean
  onSendMessage: (message: string) => void
}

export const ChatDrawer = ({ open, onClose, messages, isLoading, onSendMessage }: ChatDrawerProps) => {
  const [inputValue, setInputValue] = useState("")
  const [showGuidance, setShowGuidance] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const {
    examples,
    capabilities,
    limitations,
    hasData: hasGuidanceData,
    loading: isGuidanceLoading,
  } = useChatExamples()
  const guidanceReady = hasGuidanceData && !isGuidanceLoading

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  // Focus input when drawer opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 100)
    }
  }, [open])

  // Handle ESC key
  useEffect(() => {
    const handleKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key === "Escape" && open) {
        onClose()
      }
    }

    if (open) {
      window.addEventListener("keydown", handleKeyDown)
    }

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [open, onClose])

  const handleSubmit = () => {
    if (inputValue.trim() && !isLoading) {
      onSendMessage(inputValue)
      setInputValue("")
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  if (!open) return null

  return (
    <>
      {/* Overlay */}
      <Box position="fixed" top={0} left={0} right={0} bottom={0} bg="blackAlpha.400" zIndex={1000} onClick={onClose} />

      {/* Drawer */}
      <Box
        position="fixed"
        right={0}
        top={0}
        bottom={0}
        width={{ base: "100%", md: "400px", lg: "450px" }}
        bg="white"
        zIndex={1001}
        boxShadow="xl"
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <Flex px={4} py={3} borderBottomWidth={1} align="center" justify="space-between" bg="blue.600">
          <ChatButton />
          <Text fontWeight="bold" fontSize="lg">
            Ask Hydrologiq
          </Text>
          <CloseButton
            onClick={onClose}
            size="sm"
            context={{ label: "Close chat", description: "Close the chat", icon: FaXmark }}
          />
        </Flex>

        {/* Messages */}
        <Box flex={1} overflow="auto" p={4}>
          {guidanceReady && (
            <GuidancePanel
              examples={examples}
              capabilities={capabilities}
              limitations={limitations}
              open={showGuidance}
              onToggle={() => setShowGuidance((prev) => !prev)}
            />
          )}
          {messages.length === 0 ? (
            <VStack gap={4} py={8}>
              <ChatButton showShortcut={false} />
              <Text textAlign="center" fontSize="sm">
                Ask me anything about your assets, orders, or operational data.
              </Text>
              <VStack gap={2} fontSize="xs">
                <Text>"How many active assets do I have?"</Text>
                <Text>"Show me orders for Acme Corp"</Text>
                <Text>"Which tanks are running low?"</Text>
              </VStack>
            </VStack>
          ) : (
            <VStack gap={3} align="stretch">
              {messages.map((message) => (
                <MessageBubble key={message.id} message={message} />
              ))}
              {isLoading && (
                <Flex justify="flex-start">
                  <Box bg="white" px={4} py={3} borderRadius="lg" boxShadow="sm" borderWidth={1}>
                    <HStack gap={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm">Thinking...</Text>
                    </HStack>
                  </Box>
                </Flex>
              )}
              <div ref={messagesEndRef} />
            </VStack>
          )}
        </Box>

        {/* Input */}
        <Box p={4} borderTopWidth={1} bg="white">
          <HStack gap={2}>
            <Input
              ref={inputRef}
              placeholder="Ask a question..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isLoading}
              bg="gray.50"
              _focus={{ bg: "white" }}
            />
            <IconButton
              aria-label="Send message"
              onClick={handleSubmit}
              disabled={!inputValue.trim() || isLoading}
              variant="magic"
            >
              <FaPaperPlane />
            </IconButton>
          </HStack>
        </Box>
      </Box>
    </>
  )
}

interface GuidancePanelProps {
  examples: ChatExampleQA[]
  capabilities: string[]
  limitations: string[]
  open: boolean
  onToggle: () => void
}

const GuidancePanel = ({ examples, capabilities, limitations, open, onToggle }: GuidancePanelProps) => {
  const hasExamples = examples.length > 0
  const hasCapabilities = capabilities.length > 0
  const hasLimitations = limitations.length > 0

  if (!hasExamples && !hasCapabilities && !hasLimitations) return null

  return (
    <Box borderWidth={1} borderRadius="lg" bg="gray.50" p={3} mb={4}>
      <HStack justify="space-between" align="center">
        <Text fontWeight="semibold" fontSize="sm">
          Examples & guidance
        </Text>
        {open ? <CollapseButton onClick={onToggle} /> : <ExpandButton onClick={onToggle} />}
      </HStack>

      {open && (
        <VStack align="stretch" gap={3} mt={3}>
          {hasExamples && (
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                Example questions
              </Text>
              <VStack align="stretch" gap={2}>
                {examples.map((example, idx) => (
                  <Box key={`${example.question}-${idx}`} bg="white" borderRadius="md" borderWidth={1} p={2}>
                    <Text fontSize="sm" fontWeight="semibold">
                      Q{idx + 1}: {example.question}
                    </Text>
                    <Text fontSize="sm" mt={1}>
                      {example.answer}
                    </Text>
                  </Box>
                ))}
              </VStack>
            </Box>
          )}

          {hasCapabilities && (
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                What I can help with
              </Text>
              <VStack align="stretch" gap={1}>
                {capabilities.map((capability, idx) => (
                  <HStack key={`${capability}-${idx}`} align="start" gap={2}>
                    <Badge colorPalette="blue" variant="subtle">
                      Can
                    </Badge>
                    <Text fontSize="sm">{capability}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}

          {hasLimitations && (
            <Box>
              <Text fontSize="sm" fontWeight="semibold" mb={1}>
                What I cannot do
              </Text>
              <VStack align="stretch" gap={1}>
                {limitations.map((limitation, idx) => (
                  <HStack key={`${limitation}-${idx}`} align="start" gap={2}>
                    <Badge colorPalette="gray" variant="subtle">
                      Can't
                    </Badge>
                    <Text fontSize="sm">{limitation}</Text>
                  </HStack>
                ))}
              </VStack>
            </Box>
          )}
        </VStack>
      )}
    </Box>
  )
}

interface MessageBubbleProps {
  message: ChatMessage
}

const MessageBubble = ({ message }: MessageBubbleProps) => {
  const isUser = message.role === "user"
  const hasEntities = !!message.structured?.entities?.length
  const hasHighlights = !!message.structured?.highlights?.length

  return (
    <Flex justify={isUser ? "flex-end" : "flex-start"}>
      <Box
        maxW="85%"
        px={4}
        py={3}
        borderRadius="lg"
        bg={isUser ? "white" : "#39aed2"}
        boxShadow="sm"
        borderWidth={2}
        borderColor={isUser ? undefined : "#8f2eff"}
      >
        <VStack align="stretch" gap={2}>
          {message.structured ? (
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                p: ({ children }) => <Text fontSize="sm">{children}</Text>,
                li: ({ children }) => (
                  <li>
                    <Text as="span" fontSize="sm">
                      {children}
                    </Text>
                  </li>
                ),
              }}
            >
              {message.structured.message}
            </ReactMarkdown>
          ) : (
            <Text fontSize="sm" whiteSpace="pre-wrap">
              {message.content}
            </Text>
          )}

          {!isUser && hasHighlights && (
            <Wrap gap={2}>
              {message.structured?.highlights?.map((highlight, idx) => (
                <WrapItem key={highlight.label + idx}>
                  <Badge variant="solid" colorPalette="blue" px={2} py={1} borderRadius="md">
                    {highlight.label}: {highlight.value}
                  </Badge>
                </WrapItem>
              ))}
            </Wrap>
          )}

          {!isUser && hasEntities && (
            <>
              <Separator />
              <VStack align="stretch" gap={1}>
                {message.structured?.entities?.map((entity, idx) => {
                  const href = buildEntityHref(entity)
                  const label = `${entity.name || "Entity"} (${entity.type})`
                  return href ? (
                    <Link
                      key={`${entity.type}-${entity.id || idx}`}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="primary"
                    >
                      {label}
                    </Link>
                  ) : (
                    <Text key={`${entity.type}-${entity.id || idx}`} fontSize="sm">
                      {label}
                    </Text>
                  )
                })}
              </VStack>
            </>
          )}

          {!isUser && message.structured?.metadata?.note && (
            <Text fontSize="xs">{message.structured.metadata.note}</Text>
          )}
        </VStack>
        <Text fontSize="xs" mt={1}>
          {formatTime(message.timestamp)}
        </Text>
      </Box>
    </Flex>
  )
}

const formatTime = (date: Date): string => {
  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(date)
}

const buildEntityHref = (entity: AIEntity): string | undefined => {
  if (!entity.id) return undefined

  switch (entity.type) {
    case "asset":
      return `/assets/${entity.id}`
    case "order":
      return `/orders?order=${entity.id}`
    case "customer":
      return `/customers/${entity.id}`
    case "site":
      return `/sites/${entity.id}`
    default:
      return `/${entity.type}/${entity.id}`
  }
}
